import {
  AmplitudeContext,
  AmplitudeEventHelpers,
  AmplitudeEventConstants,
} from '@homebot/shared-util-quartermaster'
import PropTypes from 'prop-types'
import { useIntl } from 'react-intl'
import { OverlayPanel } from '../../..'
import { Redirect } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { RedirectToHomeDigest } from '../../Navigation'
import { useCallback, useRef, useContext } from 'react'
import { clientConfirmTca } from '../../../../actions/tca'
import { dispatchEvent } from '../../../../helpers/tagManager'
import MortgageCoachFullLogoSvg from './MortgageCoachFullLogoSvg'
import { OVERLAY_HELPERS } from '../../../../constants/navigation'
import { selectTcaRequested } from '../../../../store/selectors/tca'
import MortgageCoachPanelContents from './MortgageCoachPanelContents'
import { selectAuthedAsClient } from '../../../../store/selectors/auth'
import { selectCustomerType } from '../../../../store/selectors/customerProfile'
import { MortgageCoachNavigateBack } from '../../../../constants/trackingEvents'
import { selectClientHasConfirmedTca } from '../../../../store/selectors/client'

import styles from './MortgageCoachPanel.module.scss'

const MortgageCoachPanel = ({
  rootMatch,
  requestTca,
  refiDetails,
  routedFromRefi,
}) => {
  const intl = useIntl()
  const dispatch = useDispatch()
  const overlayRef = useRef(null)
  const { ampli } = useContext(AmplitudeContext)
  const requested = useSelector(selectTcaRequested)
  const isClient = useSelector(selectAuthedAsClient)
  const customerType = useSelector(selectCustomerType)
  const hasConfirmed = useSelector(selectClientHasConfirmedTca)

  const handleTracking = useCallback(() => {
    if (!requested && !hasConfirmed) {
      dispatchEvent(MortgageCoachNavigateBack, {
        eventLabel: 'Last: Request TCA',
        customerType: customerType,
      })
    }
  }, [requested, hasConfirmed, customerType])

  const handleConfirm = useCallback(() => {
    requestTca()

    if (isClient) {
      dispatch(clientConfirmTca())
      ampli?.clientRequestedInfo({
        request_type: AmplitudeEventConstants.TOTAL_COST_ANALYSIS,
        customer_type:
          AmplitudeEventHelpers.getAmplitudeCustomerType(customerType),
      })
    }
  }, [requestTca, dispatch, isClient, ampli, customerType])

  const handleClose = useCallback(() => {
    !!overlayRef.current && overlayRef.current.back()
  }, [])

  let redirectFallback
  if (!routedFromRefi) {
    if (rootMatch.url) {
      redirectFallback = <Redirect to={rootMatch.url} />
    } else {
      redirectFallback = <RedirectToHomeDigest />
    }
  }

  return (
    <OverlayPanel
      ref={overlayRef}
      back={rootMatch.url || OVERLAY_HELPERS.refinance.getOrigin()}
      path={`${OVERLAY_HELPERS.refinance.matchPath}/${OVERLAY_HELPERS.mortgageCoach.overlay}`}
      title={OVERLAY_HELPERS.getTitle('mortgageCoach', intl.formatMessage)}
      onClose={handleTracking}
      renderTitle={MortgageCoachIcon}
      render={() => (
        <MortgageCoachPanelContents
          refiDetails={refiDetails}
          hasConfirmed={hasConfirmed}
          requested={requested}
          handleConfirm={handleConfirm}
          handleClose={handleClose}
          requestTca={requestTca}
          renderRedirect={redirectFallback}
        />
      )}
    />
  )
}

const MortgageCoachIcon = () => (
  <div className={styles.mcIconWrapper}>
    <MortgageCoachFullLogoSvg />
  </div>
)

MortgageCoachIcon.propTypes = {}

MortgageCoachPanel.propTypes = {
  refiDetails: PropTypes.shape({
    type: PropTypes.string,
    years: PropTypes.number,
  }),
  rootMatch: PropTypes.shape({
    url: PropTypes.string,
  }).isRequired,
  requestTca: PropTypes.func,
  routedFromRefi: PropTypes.bool,
}

export default MortgageCoachPanel
